import React, { useContext, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import dayjs from "dayjs";

import Sidebar from "../UI/Sidebar";
import Loader from "../UI/Loader";
import ErrorMessage from "../UI/ErrorMessage";
import PostItTimelineView from "./PostItTimelineView";
import { sfQuery } from "../API";

const TicketsView = () => {
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedUser, setSelectedUser] = useState(""); // Start empty, will be set to current user
  const [availableUsers, setAvailableUsers] = useState([]);

  const context = useAuthContext();
  const { sidebar } = useParams();

  // Set default user to current logged-in user when context is ready
  useEffect(() => {
    if (context?.ready && context?.userInfo?.userFullName && !selectedUser) {
      setSelectedUser(context.userInfo.userFullName);
    }
  }, [context?.ready, context?.userInfo?.userFullName, selectedUser]);

  // Priority options for tickets (simplified)
  const priorityOptions = ["All", "Urgent", "Medium", "Normal"];

  // Status options for tickets
  const statusOptions = [
    "All",
    "Open",
    "Planned",
    "In Progress",
    "On Hold",
    "Revisions Needed",
    "Escalated",
    "Closed",
  ]; // Priority mapping for cleaner categorization
  const getPriorityCategory = (priority) => {
    if (!priority) return "Normal";

    const priorityLower = priority.toLowerCase();
    if (
      priorityLower.includes("urgent") ||
      priorityLower.includes("p0") ||
      priorityLower.includes("p1") ||
      priorityLower.includes("p2") ||
      priorityLower.includes("time-sensitive") ||
      priorityLower.includes("today")
    ) {
      return "Urgent";
    }
    if (
      priorityLower.includes("end of day") ||
      priorityLower.includes("not time-sensitive")
    ) {
      return "Medium";
    }
    return "Normal";
  };

  // Processed data for GanttView
  const processedData = useMemo(() => {
    if (!context?.ready) return [];

    // Filter out tickets that were resolved before our timeline window
    const timelineStartDate = dayjs().subtract(1, "week").startOf("day");

    const relevantTickets = tickets.filter((ticket) => {
      // Always include open tickets
      if (ticket.Status !== "Closed" && ticket.Status !== "Solved") {
        return true;
      }

      // For closed/solved tickets, only include if they were resolved during or after our timeline window
      const resolvedDateStr = ticket.ClosedDate || ticket.LastModifiedDate;
      if (!resolvedDateStr) {
        // If no resolution date, include it (might be a data issue)
        return true;
      }

      const resolvedDate = dayjs(resolvedDateStr);
      if (!resolvedDate.isValid()) {
        // If invalid date, include it (might be a data issue)
        return true;
      }

      return (
        resolvedDate.isAfter(timelineStartDate) ||
        resolvedDate.isSame(timelineStartDate, "day")
      );
    });

    console.log(
      `Timeline Filter: ${tickets.length} total tickets -> ${
        relevantTickets.length
      } relevant tickets (filtered out ${
        tickets.length - relevantTickets.length
      } old resolved tickets)`
    );

    return relevantTickets.map((ticket) => {
      const startDate = ticket.Planned_Start_Date__c || ticket.CreatedDate;

      // For completed tickets, use the actual completion date or calculate based on status
      let endDate;
      console.log("ticket", ticket);
      if (ticket.Status === "Closed" || ticket.Status === "Solved") {
        // If completed, use the actual completion date or calculate a reasonable end date
        endDate =
          ticket.ClosedDate ||
          ticket.LastModifiedDate ||
          dayjs(startDate).add(3, "day").format("YYYY-MM-DD");
      } else {
        // For open tickets, use deadline or add reasonable duration
        endDate =
          ticket.Deadline_Date__c ||
          dayjs(startDate).add(2, "day").format("YYYY-MM-DD");
      }

      return {
        id: ticket.Id,
        createdDate: startDate,
        expectedDate: endDate,
        priority: getPriorityCategory(ticket.Priority),
        originalPriority: ticket.Priority || "Not Time-Sensitive",
        status: ticket.Status,
        description:
          ticket.Description || ticket.Subject || `Ticket ${ticket.Id}`,
        assignedTo: ticket.Assigned_To__r?.Name || "Unassigned",
        type: "ticket",
      };
    });
  }, [tickets, context?.ready]);
  console.log("Processed Tickets Data:", processedData);

  // Get available users for filter - fetch from Salesforce
  useEffect(() => {
    if (!context?.ready) return;

    const fetchUsers = async () => {
      try {
        const users = await sfQuery("User", "Name", "IsActive = true", context);
        const userNames = users.map((user) => user.Name).sort();
        setAvailableUsers(userNames);
      } catch (err) {
        console.error("Error fetching users:", err);
        // Fallback to current user only
        if (context?.userInfo?.userFullName) {
          setAvailableUsers([context.userInfo.userFullName]);
        }
      }
    };

    fetchUsers();
  }, [context]); // Filtered data based on search and filters
  const filteredData = useMemo(() => {
    if (!context?.ready) return [];

    return processedData.filter((ticket) => {
      const matchesSearch =
        searchQuery === "" ||
        ticket.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.assignedTo.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesPriority =
        selectedPriority === "All" || ticket.priority === selectedPriority;
      const matchesStatus =
        selectedStatus === "All" || ticket.status === selectedStatus;
      // User filtering is handled by the query itself, so all returned tickets match the selected user

      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [
    processedData,
    searchQuery,
    selectedPriority,
    selectedStatus,
    context?.ready,
  ]);

  // Statistics (calculated from filtered data in current view only)
  const stats = useMemo(() => {
    // Get the timeline bounds to match GanttView filtering
    const timelineStart = dayjs().startOf("month").startOf("week");
    const timelineEnd = dayjs().add(1, "month").endOf("month").endOf("week");

    // Filter data to match what's shown in GanttView
    const visibleData = filteredData.filter((item) => {
      const itemStart = dayjs(item.createdDate);
      const itemEnd = dayjs(item.expectedDate);
      const itemCompleted = item.status === "Closed";

      // Include all non-completed items, and completed items only if they fall within timeframe
      if (itemCompleted) {
        return (
          itemStart.isBefore(timelineEnd) && itemEnd.isAfter(timelineStart)
        );
      }
      return true;
    });

    const total = visibleData.length;
    const urgent = visibleData.filter((t) => t.priority === "Urgent").length;
    const completed = visibleData.filter((t) => t.status === "Closed").length;
    const overdue = visibleData.filter((t) => {
      const expectedDate = dayjs(t.expectedDate);
      const isOverdue = dayjs().isAfter(expectedDate);
      const isNotCompleted = t.status !== "Closed";
      return isOverdue && isNotCompleted;
    }).length;

    return { total, urgent, completed, overdue };
  }, [filteredData]);

  // Fetch tickets data for specific user
  useEffect(() => {
    if (!context?.ready || !selectedUser) return;

    setLoading(true);
    const fetchTickets = async () => {
      try {
        console.log("Fetching tickets for user:", selectedUser);
        const ticketsData = await sfQuery(
          "Case",
          "Id, Status, Description, Subject, CreatedDate, ClosedDate,Deadline_Date__c, Planned_Start_Date__c, Assigned_To__r.Name, Priority",
          `Assigned_To__r.Name = '${selectedUser}' AND CreatedDate >= LAST_N_DAYS:30 LIMIT 1000`,
          context
        );
        console.log(
          "Fetched tickets:",
          ticketsData.length,
          "tickets for",
          selectedUser
        );
        setTickets(ticketsData);
        setError(false);
      } catch (err) {
        console.error("Error fetching tickets:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [context, selectedUser]);

  // Loading and error states
  if (!context?.ready) return <Loader />;
  if (loading) return <Loader />;
  if (error) return <ErrorMessage />;

  // Show user selection prompt if no user is selected
  if (!selectedUser) {
    return (
      <>
        {!sidebar && <Sidebar />}
        <div className={sidebar ? "contentWOS" : "content"}>
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h1
              style={{
                background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: "2.5rem",
                fontWeight: "bold",
                marginBottom: "30px",
              }}
            >
              🎫 Tickets Management
            </h1>
            <div
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "500px",
                margin: "0 auto",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h3 style={{ marginBottom: "20px", color: "#2d3748" }}>
                Select a User to View Tickets
              </h3>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{
                  padding: "12px 40px 12px 16px",
                  border: "2px solid #e0e6ed",
                  borderRadius: "10px",
                  fontSize: "16px",
                  background: "white",
                  minWidth: "250px",
                  marginBottom: "20px",
                  backgroundImage:
                    'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "12px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                <option value="">Choose a user...</option>
                {availableUsers.map((user) => (
                  <option key={user} value={user}>
                    👤 {user}
                  </option>
                ))}
              </select>
              {context?.userInfo?.userFullName && (
                <div>
                  <button
                    onClick={() =>
                      setSelectedUser(context.userInfo.userFullName)
                    }
                    style={{
                      padding: "12px 24px",
                      border: "none",
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "bold",
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 15px rgba(231, 76, 60, 0.3)",
                    }}
                  >
                    🎫 View My Tickets
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!sidebar && <Sidebar />}
      <div className={sidebar ? "contentWOS" : "content"}>
        <div style={{ padding: "20px" }}>
          <h1
            style={{
              background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: "2.5rem",
              fontWeight: "bold",
              marginBottom: "30px",
            }}
          >
            🎫 Tickets Management
          </h1>

          {/* Statistics Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "30px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(231, 76, 60, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.total}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                Total Tickets
              </p>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(255, 107, 107, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.urgent}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                🔥 Urgent &amp; Time-Sensitive
              </p>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #00b894 0%, #00a085 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(0, 184, 148, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.completed}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                ✅ Completed
              </p>
            </div>
            <div
              style={{
                background: "linear-gradient(135deg, #e17055 0%, #d63031 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "15px",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(225, 112, 85, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "2rem",
                  color: "white",
                }}
              >
                {stats.overdue}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, color: "white" }}>
                ⚠️ Overdue
              </p>
            </div>
          </div>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              marginBottom: "30px",
              flexWrap: "wrap",
              alignItems: "center",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              padding: "20px",
              borderRadius: "15px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <input
              type="text"
              placeholder="🔍 Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: "12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                minWidth: "250px",
                fontSize: "14px",
                background: "white",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            />

            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{
                padding: "12px 40px 12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                fontSize: "14px",
                background: "white",
                minWidth: "200px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              <option value="">Select User...</option>
              {availableUsers.map((user) => (
                <option key={user} value={user}>
                  👤 {user}
                </option>
              ))}
            </select>

            {context?.userInfo?.userFullName &&
              selectedUser !== context.userInfo.userFullName && (
                <button
                  onClick={() => setSelectedUser(context.userInfo.userFullName)}
                  style={{
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(231, 76, 60, 0.3)",
                  }}
                  title="Show only my tickets"
                >
                  🎫 My Tickets
                </button>
              )}

            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              style={{
                padding: "12px 40px 12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                fontSize: "14px",
                background: "white",
                minWidth: "130px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {priorityOptions.map((priority) => (
                <option key={priority} value={priority}>
                  🎯 {priority}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                padding: "12px 40px 12px 16px",
                border: "2px solid #e0e6ed",
                borderRadius: "10px",
                fontSize: "14px",
                background: "white",
                minWidth: "130px",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  📊 {status}
                </option>
              ))}
            </select>
          </div>

          {/* Gantt Chart */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              padding: "30px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              marginBottom: "30px",
            }}
          >
            <h2
              style={{
                fontSize: "1.8rem",
                fontWeight: "bold",
                marginBottom: "20px",
                background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              📋 Tickets Post-It Timeline (4 Week View)
            </h2>
            <PostItTimelineView
              data={filteredData}
              type="tickets"
              selectedUser={selectedUser}
              enableStickyHeader={true}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketsView;
